import {
  Skeleton,
  Text,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Flex,
  useToast,
} from '@chakra-ui/react';
import React from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  BankAccountWithInstitutionAndDesignationGQLType,
  FundingRequestComponentTypeEnumGQLType,
  UpdateFundingRequestComponentChangesetGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';
import {
  FundingRequestComponentContainer,
  FundingRequestComponentDefaultTargetFundingDates,
} from 'types/fundingRequests';

import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { SimpleDatePicker } from '~/packages/common-frontend/components/SimpleDatePicker';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import {
  getUnexpectedErrorMessage,
  bankAccountToDisplayString,
  dollarNumberWithCommas,
} from '~/packages/common-frontend/utils';
import { fundingRequestKeyToLabel } from '~/packages/funding-requests/constants';
import { useGetOrganizationsByIdsQuery } from '~/packages/organization/requests/organization.gql.generated';

export type TargetFundingDateEditFunction = (
  id: string,
  type: FundingRequestComponentTypeEnumGQLType,
  fullDateStr: Util.FullDateStr,
) => void;

/**
 * Renders a funding request component table row
 */
const FundingRequestComponentTableRow = ({
  bankAccountMap,
  fundingRequestComponent,
  defaultTargetFundingDate,
  onTargetFundingDateEdit,
  componentFrequencyMessage,
  addedContributionMessage,
  closingDate,
}: {
  bankAccountMap: Map<
    string,
    BankAccountWithInstitutionAndDesignationGQLType & {
      organizationName?: string;
    }
  >;
  fundingRequestComponent: UpdateFundingRequestComponentChangesetGQLType;
  defaultTargetFundingDate: undefined | Util.FullDateStr;
  onTargetFundingDateEdit: undefined | TargetFundingDateEditFunction;
  componentFrequencyMessage: undefined | string;
  addedContributionMessage: undefined | string;
  closingDate?: Util.FullDateStr | null;
}) => {
  const flags = useFeatureFlags();

  // Target funding date
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const today = new Date();
  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const minDate =
    flags.allowHistoricalFundingRequest && closingDate
      ? commonDate.fullDateStrToDate(closingDate)
      : today;
  const maxDate = new Date(today.getFullYear(), today.getMonth() + 2, 0);
  const selectedDate = fundingRequestComponent.targetFundingDate
    ? commonDate.fullDateStrToDate(fundingRequestComponent.targetFundingDate)
    : undefined;

  return (
    <Tr>
      <Td>
        {fundingRequestKeyToLabel[fundingRequestComponent.type]}
        {componentFrequencyMessage && (
          <Text
            position="absolute"
            width="180px"
            color="red.400"
            fontSize="12px"
            lineHeight="base"
          >
            {componentFrequencyMessage}
          </Text>
        )}
      </Td>
      <Td>
        {dollarNumberWithCommas(fundingRequestComponent.amount, 2)}
        {addedContributionMessage && (
          <Text
            position="absolute"
            width="180px"
            fontSize="12px"
            lineHeight="base"
            whiteSpace="pre-line"
          >
            {addedContributionMessage}
          </Text>
        )}
      </Td>
      <Td>
        {bankAccountToDisplayString(
          bankAccountMap.get(fundingRequestComponent.sourceBankAccountId),
        )}
      </Td>
      <Td>
        {bankAccountToDisplayString(
          bankAccountMap.get(fundingRequestComponent.destinationBankAccountId),
        )}
      </Td>
      <Td>
        {!onTargetFundingDateEdit &&
          fundingRequestComponent.targetFundingDate &&
          commonDate.fullDateStrToHumanDateString(
            fundingRequestComponent.targetFundingDate,
          )}

        {onTargetFundingDateEdit && (
          <Flex>
            <SimpleDatePicker
              width="220px"
              tz={clientTimeZone}
              dateFormat="MMMM dd, yyyy"
              minDate={minDate}
              maxDate={maxDate}
              selectedDate={selectedDate}
              showTimeSelect={false}
              dayClassName={(date) =>
                closingDate && date < todayStart ? 'past-selectable-date' : null
              }
              setSelectedDate={(date: Date | null) => {
                if (!date) return;
                const fullDateStr = commonDate.dateToFullDateString(
                  date,
                  clientTimeZone,
                );
                onTargetFundingDateEdit(
                  fundingRequestComponent.id,
                  fundingRequestComponent.type,
                  fullDateStr,
                );
              }}
            />
          </Flex>
        )}
        {/* If editable and a default target funding date is present, display a warning only for draw request components when target funding date is adjusted */}
        {onTargetFundingDateEdit &&
          defaultTargetFundingDate &&
          fundingRequestComponent.targetFundingDate !==
            defaultTargetFundingDate && (
            <Text
              position="absolute"
              width="180px"
              color="red.400"
              fontSize="12px"
              lineHeight="base"
            >
              Warning: This change requires approval from your lender(s).
              Default:{' '}
              {commonDate.fullDateStrToHumanDateString(
                defaultTargetFundingDate,
              )}
            </Text>
          )}
      </Td>
    </Tr>
  );
};

/**
 * React component to display the funding request summary
 */
export const FundingRequestComponentsTable = ({
  bankAccountMap,
  fundingRequestComponents,
  isCollectionsRecycleEnabled,
  isVoluntaryPaydownAllowed,
  maxFundingRequestAmount,
  defaultTargetFundingDates = {},
  onTargetFundingDateEdit,
  drawRequestFrequencyMessage,
  prefundedDrawContributionMessage,
  closingDate,
}: {
  bankAccountMap: Map<
    string,
    BankAccountWithInstitutionAndDesignationGQLType & {
      organizationName?: string;
    }
  >;
  fundingRequestComponents: FundingRequestComponentContainer;
  isCollectionsRecycleEnabled: boolean;
  isVoluntaryPaydownAllowed: boolean;
  maxFundingRequestAmount: number;
  defaultTargetFundingDates?: FundingRequestComponentDefaultTargetFundingDates;
  onTargetFundingDateEdit?: TargetFundingDateEditFunction;
  drawRequestFrequencyMessage?: string;
  prefundedDrawContributionMessage?: string;
  closingDate?: Util.FullDateStr | null;
}) => {
  const toast = useToast();
  const {
    data: organizationsData,
    loading: organizationsLoading,
  } = useGetOrganizationsByIdsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      organizationIds: Array.from(
        bankAccountMap.values(),
        (bankAccount) => bankAccount.organizationId,
      ),
    },
  });
  if (organizationsLoading || !organizationsData) {
    return (
      <Skeleton data-testid="loading-funding-request-table" height="200px" />
    );
  }

  const addOrganizationNameToBankAccount = (
    bankAccount: BankAccountWithInstitutionAndDesignationGQLType & {
      organizationName?: string;
    },
  ) => {
    const organization = organizationsData.organizationsByIds.find(
      (org) => org.id === bankAccount.organizationId,
    );
    const organizationName = organization ? organization.name : '';
    return {
      ...bankAccount,
      organizationName,
    };
  };
  bankAccountMap.forEach((bankAccount, key) => {
    bankAccountMap.set(key, addOrganizationNameToBankAccount(bankAccount));
  });

  const renderedComponents = Object.entries(fundingRequestComponents).reduce(
    (acc, [componentKey, componentArray]) => {
      const renderComponents = componentArray.filter((currComponent) => {
        const { type } = currComponent;

        if (currComponent.amount === 0) return false;

        switch (type) {
          case 'curePaydown':
            if (maxFundingRequestAmount < 0) return true;
            return false;
          case 'collectionsPaydown':
            if (
              isVoluntaryPaydownAllowed === true ||
              (isVoluntaryPaydownAllowed === false &&
                maxFundingRequestAmount <= 0)
            )
              return true;
            return false;
          case 'collectionsRecycle':
            return isCollectionsRecycleEnabled === true;
          default:
            return true;
        }
      });
      const renderedComponentPairs: [
        string,
        UpdateFundingRequestComponentChangesetGQLType,
      ][] = renderComponents.map((component) => {
        return [componentKey, component];
      });

      return [...acc, ...renderedComponentPairs];
    },
    [] as [string, UpdateFundingRequestComponentChangesetGQLType][],
  );

  const getComponentFrequencyMessage = (
    componentType: string,
  ): string | undefined => {
    switch (componentType) {
      case 'drawRequest':
        return drawRequestFrequencyMessage;
      default:
        return undefined;
    }
  };

  const getComponentAddedContributionMessage = (
    componentType: string,
  ): string | undefined => {
    switch (componentType) {
      case 'drawRequest':
        return prefundedDrawContributionMessage;
      default:
        return undefined;
    }
  };

  return (
    <Table mb="40px" borderWidth="1px">
      <Thead>
        <Tr>
          <Th>Transaction</Th>
          <Th>Amount</Th>
          <Th>Source</Th>
          <Th>Destination</Th>
          <Th>Funding Date</Th>
        </Tr>
      </Thead>
      <Tbody>
        {renderedComponents.map(([, fundingRequestComponent]) => {
          return (
            <FundingRequestComponentTableRow
              key={`key-${Math.random()}`}
              bankAccountMap={bankAccountMap}
              fundingRequestComponent={fundingRequestComponent}
              defaultTargetFundingDate={
                defaultTargetFundingDates &&
                defaultTargetFundingDates[fundingRequestComponent.id]
              }
              onTargetFundingDateEdit={onTargetFundingDateEdit}
              componentFrequencyMessage={getComponentFrequencyMessage(
                fundingRequestComponent.type,
              )}
              addedContributionMessage={getComponentAddedContributionMessage(
                fundingRequestComponent.type,
              )}
              closingDate={closingDate}
            />
          );
        })}
      </Tbody>
    </Table>
  );
};
